import type { IEnvironment } from '@tarslib/types/lib/environment';
import { EnvironmentMockType } from '@tarslib/types/lib/environment';
import type { IHttpFolderMap } from '@tarslib/types/lib/httpFolder';
import type { ServerItem } from '@tarslib/types/lib/projectSetting';

export enum MultiServer {
  InheritParent = '',
  Default = 'default',
}

const ROOT_FOLDER_ID = 0;

const checkExist = (serverId: string, servers?: ServerItem[]) => {
  const index = servers?.findIndex(({ id }) => id === serverId) ?? -1;
  return index > -1;
};

export const checkInheritParent = (serverId: string | undefined, servers?: ServerItem[]) => {
  if (!serverId) return true;
  if (serverId === MultiServer.InheritParent) {
    return true;
  }
  if (!checkExist(serverId, servers)) {
    return true;
  }
  return false;
};

export const getTargetFolder = ({
  moduleId,
  folderId,
  folderMap,
  servers,
}: {
  moduleId?: number;
  folderId: number;
  folderMap: IHttpFolderMap;
  servers?: ServerItem[];
}): any => {
  if (folderId === ROOT_FOLDER_ID && moduleId) {
    return Object.values(folderMap).find((x) => x.type === 'root' && x.moduleId === moduleId) || {};
  }
  const folder = folderMap[folderId];

  if (!folder) return {};

  const { parentId, serverId } = folder;
  if (checkInheritParent(serverId, servers)) {
    return getTargetFolder({
      moduleId,
      folderId: parentId,
      folderMap,
      servers,
    });
  }

  return folder;
};

export const getRequestBaseUrl = ({
  serverId,
  environment,
  servers,
  path,
}: {
  serverId: string;
  servers?: ServerItem[];
  environment?: IEnvironment;
  path?: string;
}) => {
  if (!environment) return '';

  if (path && /^https?:\/\//.test(path)) {
    return '';
  }

  const { baseUrls = {}, type } = environment;

  if (type === 'mock') {
    return baseUrls[MultiServer.Default] || '';
  }

  if (checkExist(serverId, servers)) {
    return baseUrls[serverId] || '';
  }

  if (path && path.startsWith('{{')) {
    return '';
  }

  return baseUrls[MultiServer.Default] || '';
};

export const getWebsocketRequestBaseUrl = ({
  serverId,
  environment,
  path,
}: {
  serverId: string;
  environment?: IEnvironment;
  path?: string;
}) => {
  if (
    !environment ||
    (path && (path.startsWith('{{') || path.startsWith('ws://') || path.startsWith('wss://')))
  ) {
    return '';
  }

  const { websocketBaseUrls = {}, type } = environment;

  if (type && EnvironmentMockType.includes(type)) {
    return '';
  }

  return websocketBaseUrls[serverId] || '';
};
